import { Extension, findParentNodeClosestToPos } from '@tiptap/core';
import type { Transaction } from '@tiptap/pm/state';
import { Plugin } from '@tiptap/pm/state';
import { applyColgroupWidths, parseColWidthToNumber } from './colgroup-widths.util';

export type TableCellTextAlign = 'left' | 'center' | 'right' | 'justify';
export type TableCellVerticalAlign = 'top' | 'middle' | 'bottom';

type CellContext = {
    pos: number;
    node: any;
};

const parseStyle = (value: string | null | undefined): Record<string, string> => {
    if (!value) {
        return {};
    }

    return value
        .split(';')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .reduce<Record<string, string>>((acc, entry) => {
            const [key, ...rest] = entry.split(':');
            if (!key || rest.length === 0) {
                return acc;
            }
            const styleKey = key.trim().toLowerCase();
            const styleValue = rest.join(':').trim();
            if (styleKey && styleValue) {
                acc[styleKey] = styleValue;
            }
            return acc;
        }, {});
};


const serializeStyle = (styles: Record<string, string>): string => {
    return Object.entries(styles)
        .map(([key, val]) => `${key}: ${val}`)
        .join('; ');
};

const setStyleProperty = (value: string | null | undefined, key: string, nextValue: string | null): string | null => {
    const styles = parseStyle(value);

    if (nextValue == null || nextValue === '') {
        delete styles[key];
    } else {
        styles[key] = nextValue;
    }

    const nextStyle = serializeStyle(styles);
    return nextStyle.length > 0 ? `${nextStyle};` : null;
};

const getCellContext = (tr: Transaction): CellContext | null => {
    const cell =
        findParentNodeClosestToPos(tr.selection.$from, (node) =>
            node.type.name === 'tableCell' || node.type.name === 'tableHeader',
        ) ?? null;

    if (!cell) {
        return null;
    }

    return {
        pos: cell.pos,
        node: cell.node,
    };
};

const setCellAttributes = (tr: Transaction, pos: number, nextAttrs: Record<string, unknown>): void => {
    const node = tr.doc.nodeAt(pos);
    if (!node) {
        return;
    }

    tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        ...nextAttrs,
    });
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        tableCellStyle: {
            setTableCellTextAlign: (value: TableCellTextAlign | null) => ReturnType;
            setTableCellVerticalAlign: (value: TableCellVerticalAlign | null) => ReturnType;
        };
    }
}

export const TableCellStyle = Extension.create({
    name: 'tableCellStyle',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    transformPastedHTML: (html) => applyColgroupWidths(html),
                },
            }),
        ];
    },

    addGlobalAttributes() {
        return [
            {
                types: ['tableCell', 'tableHeader'],
                attributes: {
                    style: {
                        default: null,
                        parseHTML: (element) => element.getAttribute('style'),
                        renderHTML: (attributes) =>
                            attributes['style'] ? { style: attributes['style'] } : {},
                    },
                    width: {
                        default: null,
                        parseHTML: (element) => element.getAttribute('width'),
                        renderHTML: (attributes) =>
                            attributes['width'] ? { width: attributes['width'] } : {},
                    },
                    colwidth: {
                        default: null,
                        parseHTML: (element) => {
                            const styleWidth = element.getAttribute('style');
                            const styles = parseStyle(styleWidth);
                            const width = parseColWidthToNumber(styles['width'] || element.getAttribute('width'));
                            return width != null ? [width] : null;
                        },
                        renderHTML: (attributes) =>
                            Array.isArray(attributes['colwidth'])
                                ? { 'data-colwidth': attributes['colwidth'].join(',') }
                                : {},
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setTableCellTextAlign:
                (value: TableCellTextAlign | null) =>
                    ({ tr, dispatch }) => {
                        const context = getCellContext(tr);
                        if (!context) {
                            return false;
                        }

                        const nextStyle = setStyleProperty(context.node.attrs?.style, 'text-align', value);
                        setCellAttributes(tr, context.pos, { style: nextStyle });

                        if (dispatch) {
                            dispatch(tr);
                        }

                        return true;
                    },
            setTableCellVerticalAlign:
                (value: TableCellVerticalAlign | null) =>
                    ({ tr, dispatch }) => {
                        const context = getCellContext(tr);
                        if (!context) {
                            return false;
                        }

                        const nextStyle = setStyleProperty(context.node.attrs?.style, 'vertical-align', value);
                        setCellAttributes(tr, context.pos, { style: nextStyle });

                        if (dispatch) {
                            dispatch(tr);
                        }

                        return true;
                    },
        };
    },
});
